import { useCallback, useEffect, useMemo, useState } from 'react'

import { SendersInfo } from 'app/dataTypes/transfer/senders'
import { RecipientsInfo } from 'app/dataTypes/transfer/recipients'
import { RecipientUID, SenderUID } from 'app/dataTypes/transfer/uid'
import { useTransfer } from 'app/dataProviders/transfer/hooks'

import useCallbackRef from 'app/common/hooks/useCallbackRef'
import { Control } from './Control'
import TransferDropdown from './TransferDropdown'

import styles from './Transfer.module.scss';


type Props = {
  senders: SendersInfo
  recipients: RecipientsInfo
  loading: boolean
  onReset?: () => void
}

export const Transfer = ({ senders, recipients, loading, onReset }: Props) => {
  const { activeSenders } = senders
  const { activeRecipientsBySenderUID } = recipients

  const [fromUID, setFromUID] = useState<SenderUID | null>(null)
  const [toUID, setToUID] = useState<RecipientUID | null>(null)

  const onResetRef = useCallbackRef(onReset)

  const reset = useCallback(() => {
    setFromUID(null)
    setToUID(null)
    onResetRef.current?.()
  }, [onResetRef])

  const activeRecipients = fromUID ? activeRecipientsBySenderUID[fromUID] : null

  const transfer = useTransfer()

  const sender = useMemo(() => {
    return (fromUID && activeSenders?.find(({ UID }) => UID === fromUID)) || null
  }, [fromUID, activeSenders])

  const recipient = useMemo(() => {
    return (toUID && activeRecipients?.find(({ UID }) => UID === toUID)) || null
  }, [toUID, activeRecipients])

  useEffect(() => {
    if (fromUID && sender) {
      return
    }
    setFromUID(null)
  }, [sender]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (toUID && recipient) {
      return
    }
    setToUID(null)
  }, [recipient]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.root}>
      <TransferDropdown
        type="from"
        disabled={loading}
        items={activeSenders}
        selectedUID={fromUID}
        onChange={setFromUID}
      />

      <Control
        sender={sender}
        recipient={recipient}
        loading={loading}
        onSubmit={transfer}
        onReset={reset}
      />

      <TransferDropdown
        type="to"
        disabled={loading || !activeRecipients}
        items={activeRecipients}
        selectedUID={toUID}
        onChange={setToUID}
      />
    </div>
  )
}
